import { ActorDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.actor";
import {
  tableNameMatcher,
  useTableChangeVersion,
} from "@-/dev.improve.haniverse.lib.database.change";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import arrayLength from "@-/com.npmjs.extra-array__length";

const REVIEWED_ROWS_COUNT_TABLES = ["user_card_review"] as const;

/** Start (inclusive) and end (exclusive) of the current local day, as UTC ISO. */
function getLocalDayBoundsIso(now: Date): { endIso: string; startIso: string } {
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  return { endIso: end.toISOString(), startIso: start.toISOString() };
}

export function useGetReviewedRowsCountQueryKey() {
  const [actorDb] = useContextSelectors(ActorDatabaseContext, (c) => c.db);
  const withTableChangeVersion = useTableChangeVersion(
    tableNameMatcher(REVIEWED_ROWS_COUNT_TABLES),
  );
  return () => withTableChangeVersion(["reviewedRowsCount", { actorDb }] as const);
}

export const [useReviewedRowsCountQueryOptions, reviewedRowsCountQueryFn] = createUseQueryOptions(
  useGetReviewedRowsCountQueryKey,
  async ({ queryKey: [_, { actorDb }] }) => {
    const { endIso, startIso } = getLocalDayBoundsIso(new Date());
    // The local-day bounds must be computed at FETCH time (a midnight rollover between render
    // and fetch would count the wrong day), so this read can't be expressed as a render-built
    // `actorQueries` config; a bounded direct read is deliberate here.
    // oxlint-disable-next-line han-actor-db/no-raw-actor-db-in-query-fn -- see above
    const reviewedRows = await actorDb(import.meta.location).query.userCardReview.findMany({
      columns: { id: true },
      where: { reviewedAt: { gte: startIso, lt: endIso } },
    });
    return arrayLength(reviewedRows);
  },
);
